import { History, MapPin, Sparkles, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { customFieldHtmlHasText } from 'utils/certificateHtml'

/**
 * VRG-782 — the certificate's sections: what the record is made of, in one place.
 *
 * ## ⚠️ There is no tab bar, and the ticket is written as though there is
 *
 * VRG-782's acceptance criteria say "the four spine TABS cannot be removed or
 * reordered" and "a brand TAB appends after the spine tabs". The four tabs were
 * removed by VERA-315 Phase 4, before this ticket was written. What exists now is:
 *
 *   * **Product** — not a tab and not a row. It IS the certificate page: the facts,
 *     the description, the brand's custom fields, rendered inline.
 *   * **Provenance / Scans / Activity** — three chevron rows under a "History"
 *     label, each pushing to its own route (`/certificates/:id/<path>`).
 *
 * The file names in `components/certificateTabs/tabs/` still say Tab, which is how
 * the ticket came to describe a bar that is not there. They are left alone — the
 * rename is churn across five import sites and changes nothing a user sees.
 *
 * So this registry models SECTIONS, and each section says how it renders. The
 * criteria are met in substance: one registry, a spine that cannot be displaced, and
 * brand sections appended after it.
 *
 * ## Why non-removability is structural rather than configured
 *
 * The spine is a module-private tuple and {@link certificateSections} is the only way
 * to read it. That function takes the certificate — nothing else — so there is no
 * parameter a caller could pass to suppress a spine section, reorder one, or insert
 * ahead of one. A brand's sections arrive from {@link brandSections}, which returns
 * only its own entries and never sees the spine.
 *
 * This is the point of the whole exercise rather than a detail of it. The alternative
 * the certificate was heading towards — VERA-272's drag-and-drop module canvas — makes
 * layout a per-brand variable, and a certificate is an identity document: a reader
 * who has seen one knows where the next one's status sits. Consistent placement is
 * itself a fraud signal, and it is also what lets `data-tour` anchors
 * (working-conventions §56), the docs reverse-index (§61) and glossary tooltips (§44)
 * point at a position that will still be there next release.
 */

/** How a section reaches the reader. */
export type CertificateSectionKind =
  /** Rendered in the certificate page itself. Today: the record — Product. */
  | 'inline'
  /** A chevron row that pushes to `/certificates/:id/<path>`. */
  | 'subview'

export interface CertificateSection {
  /** Stable identity. Not shown to anyone; used by the row key and by the guard. */
  id: string
  /** Shown on the row and as the pushed screen's title. */
  label: string
  kind: CertificateSectionKind
  /**
   * The path segment under `/certificates/:certificateId/`. Present on `subview`
   * sections and absent on `inline` ones, which have no route of their own.
   */
  path?: string
  /** Row glyph. Absent for `inline`, which renders no row. */
  Icon?: LucideIcon
  /**
   * VRG-418 — the glossary entry the row's `(i)` explains, where the label is a term
   * of art a first-time reader cannot guess. `undefined` means ordinary English, not
   * "not done yet": inventing a definition for symmetry is how a glossary stops
   * being worth opening (working-conventions §44).
   */
  termId?: string
}

/**
 * THE SPINE. Four sections, this order, every certificate.
 *
 * Module-private on purpose — see the note above. Nothing exported takes it as an
 * argument, so nothing can hand back a shorter or re-sorted version of it.
 */
const SPINE: readonly CertificateSection[] = [
  {
    id: 'product',
    label: 'Product',
    kind: 'inline',
  },
  // Glyphs name what each screen CONTAINS, and are deliberately not the app-nav set:
  // `Activity` uses `History` rather than the nav's pulse glyph, because the nav's
  // Activity is cross-certificate and this one is this object's own log.
  {
    id: 'provenance',
    label: 'Provenance',
    kind: 'subview',
    path: 'provenance',
    Icon: Users,
    termId: 'provenance',
  },
  {
    id: 'scans',
    label: 'Scans',
    kind: 'subview',
    path: 'scans',
    Icon: MapPin,
    termId: 'scan',
  },
  { id: 'activity', label: 'Activity', kind: 'subview', path: 'activity', Icon: History },
] as const

/** The spine's ids, in order — the guard's assertion target. */
export const CERTIFICATE_SPINE_IDS = SPINE.map(s => s.id)

/**
 * The shape a brand section reads off. Deliberately narrow: a function that needs the
 * whole certificate to decide whether a section exists is reaching for something it
 * should be given instead.
 */
interface BrandSectionSource {
  product?: { experience_content?: string | null } | null
}

/**
 * The brand's own sections, appended after the spine.
 *
 * ## Why "enabled" is the presence of content, not a flag
 *
 * VRG-782 asks for an Experience section "enableable per SKU". A boolean column would
 * be the literal reading and is the worse design, for two reasons that both bite:
 *
 *   1. **Enabled-and-empty is reachable.** A brand flips the switch, means to write
 *      the copy later, and every certificate for that SKU now offers a row that opens
 *      a blank screen. On an identity document an empty section does not read as
 *      "coming soon", it reads as something missing from the record.
 *   2. **Two sources of truth for one fact.** The flag and the body can disagree, and
 *      nothing reconciles them. Emptying the body leaves the row; clearing the flag
 *      orphans the copy.
 *
 * So the body's emptiness IS the switch. It cannot disagree with itself, and the
 * authoring gesture — type something, or clear it — is the enabling gesture.
 *
 * Measured on the HTML rather than the string: the rich-text editor stores `<p></p>`
 * for an emptied field, which is 7 characters of nothing. `customFieldHtmlHasText`
 * sanitizes and then checks for actual text, so markup alone does not count as
 * content (the same test the brand custom-field slot uses).
 */
/**
 * ⚠️ EXPERIENCE IS OFF — VRG-787-Certificate.Experience.Section.
 *
 * Spencer's call, 2026-10-31, while reviewing the section on a live certificate: hidden
 * on BOTH surfaces (this one and the product form's authoring card) until the design is
 * settled. Two things were wrong with it as shipped and neither is a bug to patch:
 *
 *   1. It sits under a label reading **HISTORY**, beside Provenance / Scans / Activity.
 *      Those three are the record's own audit trail — things that HAPPENED to this
 *      certificate, each with a timestamp. A brand's story about its atelier is not one
 *      of them, and grouping it there quietly asserts that it is, on a document whose
 *      whole job is to distinguish attested fact from marketing.
 *   2. The row is a generic sparkle with no stated meaning.
 *
 * Both were my call rather than anything the AC asked for, which is why this turns off
 * rather than gets adjusted — the question "where does brand narrative belong on an
 * identity document" has not been answered, and guessing a second time is not better
 * than guessing the first.
 *
 * NOTHING IS DELETED. `products.experience_content` keeps its column, its migration and
 * any copy already authored; `brandSections` keeps its shape so the spine-plus-appended
 * structure VRG-782 exists to enforce is still exercised. Flip this to `true` and the
 * section returns exactly as it was.
 *
 * EXPORTED because the owner's row is only HALF of it. A public visitor gets the same
 * copy rendered INLINE on the certificate (`CertificatePage/index.tsx`,
 * `experienceHtmlPresent`), which does not go through `brandSections` at all — the two
 * paths are deliberately different, and a flag that only reached one of them would hide
 * the section from its author while still publishing it to strangers. One switch, read
 * at both sites. The platform's authoring card has its own `EXPERIENCE_ENABLED`, in the
 * other app, which cannot import this one; its docblock names this flag so the pair is
 * findable from either side.
 */
export const EXPERIENCE_SECTION_ENABLED = false

export function brandSections(certificate: BrandSectionSource | null | undefined): CertificateSection[] {
  const sections: CertificateSection[] = []

  if (EXPERIENCE_SECTION_ENABLED && customFieldHtmlHasText(certificate?.product?.experience_content)) {
    sections.push({
      id: 'experience',
      label: 'Experience',
      kind: 'subview',
      path: 'experience',
      Icon: Sparkles,
      // No `termId`: "Experience" is ordinary English, and the section's content is
      // the brand's own words rather than a Vera concept needing a definition.
    })
  }

  return sections
}

/**
 * Every section this certificate has, spine first.
 *
 * The spine is spread ahead of the brand's sections at this one site, which is the
 * whole enforcement: there is no ordering parameter and no filter hook, so appending
 * is the only thing a brand section can do.
 */
export function certificateSections(
  certificate: BrandSectionSource | null | undefined,
): CertificateSection[] {
  return [...SPINE, ...brandSections(certificate)]
}

/**
 * The sections that render as rows — i.e. everything except the inline record.
 *
 * This is what the certificate page's "History" list maps over. A brand section
 * added to {@link brandSections} appears there with no edit to the page, which is
 * VRG-782's "adding a section requires no edit to the certificate page" criterion;
 * a NEW ROUTE still has to be registered in `App.tsx`, because a pushed screen needs
 * an element to render and react-router has no way to infer one. Stated rather than
 * glossed: the registry removes the page edit, not the route edit.
 */
export function certificateSubViewSections(
  certificate: BrandSectionSource | null | undefined,
): CertificateSection[] {
  return certificateSections(certificate).filter(s => s.kind === 'subview')
}
